import { useEffect, useState } from 'react';
import {
  FormControl,
  IconButton,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { LinkOff, Link } from '@mui/icons-material';
import { useNode } from '@craftjs/core';
import useResponsiveValues, { ResponsiveValue } from '@/components/editor/hooks/useResponsiveValues';
import './styles.scss';

export type Spacing = Padding & Margin;

export interface Margin {
  marginTop?: string | ResponsiveValue<string>;
  marginRight?: string | ResponsiveValue<string>;
  marginBottom?: string | ResponsiveValue<string>;
  marginLeft?: string | ResponsiveValue<string>;
}

export interface Padding {
  paddingTop?: string | ResponsiveValue<string>;
  paddingRight?: string | ResponsiveValue<string>;
  paddingBottom?: string | ResponsiveValue<string>;
  paddingLeft?: string | ResponsiveValue<string>;
}

type Type = 'margin' | 'padding';

interface Props {
  margin?: boolean;
  padding?: boolean;
}

const SpacingGridSettings = ({ margin = true, padding = true }: Props) => {
  const [type, setType] = useState<Type>('padding');
  const [linked, setLinked] = useState(false);

  const responsive = useResponsiveValues();

  const {
    props,
    actions: { setProp },
  } = useNode((node) => ({
    props: node.data.props,
  }));

  const handleChange = (e: { target: { name: string; value: string } }) => {
    const target = `${type}${e.target.name}`;
    const value = e.target.value;

    setProp((props: Margin & Padding) => {
      if (linked) {
        if (type === 'margin') {
          props.marginTop = responsive.setValue(props.marginTop, value);
          props.marginRight = responsive.setValue(props.marginRight, value);
          props.marginBottom = responsive.setValue(props.marginBottom, value);
          props.marginLeft = responsive.setValue(props.marginLeft, value);
        } else {
          props.paddingTop = responsive.setValue(props.paddingTop, value);
          props.paddingRight = responsive.setValue(props.paddingRight, value);
          props.paddingBottom = responsive.setValue(props.paddingBottom, value);
          props.paddingLeft = responsive.setValue(props.paddingLeft, value);
        }
      } else {
        props[target] = responsive.setValue(props[target], value)
      }
      return props;
    });
  };

  useEffect(() => {
    if (margin && padding) {
      setType('padding');
    }

    if (margin && !padding) {
      setType('margin');
    }

    if (!margin && padding) {
      setType('padding');
    }
  }, [margin, padding])

  return (
    <>
      {margin && padding ? (
        <>
          <FormControl fullWidth={true}>
            <ToggleButtonGroup
              color="primary"
              value={type}
              exclusive
              onChange={(_, value) => {
                return setType(value);
              }}
              fullWidth
              size="small"
            >
              <ToggleButton size="small" value="padding">
                Padding
              </ToggleButton>
              <ToggleButton size="small" value="margin">
                Margin
              </ToggleButton>
            </ToggleButtonGroup>
          </FormControl>
          <br />
          <br />
        </>
      ) : null}
      <div className="spacing-grid__container">
        {/* top */}
        <div className="spacing-grid__item-a">
          <TextField
            type="number"
            name="Top"
            size="small"
            value={
              responsive.getValue(
                type === 'margin' 
                  ? props.marginTop ?? '0' 
                  : props.paddingTop ?? '0'
              )
            }
            onChange={handleChange}
          />
        </div>
        {/* left */}
        <div className="spacing-grid__item-b">
          <TextField
            type="number"
            name="Left"
            size="small"
            value={
              responsive.getValue(
                type === 'margin' 
                  ? props.marginLeft ?? '0' 
                  : props.paddingLeft ?? '0'
              )
            }
            onChange={handleChange}
          />
        </div>
        {/* right */}
        <div className="spacing-grid__item-c">
          <TextField
            type="number"
            name="Right"
            size="small"
            value={
              responsive.getValue(
                type === 'margin' 
                  ? props.marginRight ?? '0' 
                  : props.paddingRight ?? '0'
              )
            }
            onChange={handleChange}
          />
        </div>
        {/* bottom */}
        <div className="spacing-grid__item-d">
          <TextField
            type="number"
            name="Bottom"
            size="small"
            value={
              responsive.getValue(
                type === 'margin' 
                  ? props.marginBottom ?? '0' 
                  : props.paddingBottom ?? '0'
              )
            }
            onChange={handleChange}
          />
        </div>
        {/* icon */}
        <div className="spacing-grid__item-e">
          <Stack justifyContent={'center'} alignItems={'center'} height={60}>
            <IconButton onClick={() => setLinked(!linked)}>
              {linked ? <Link /> : <LinkOff />}
            </IconButton>
          </Stack>
        </div>
      </div>
    </>
  );
};

export default SpacingGridSettings;
